import { Box, Grid, Typography } from '@mui/material';
import React from 'react';
import useWidth from '../../../components/Hooks/useWidth';
import { useSignedContractsStore } from '../../../stores/SignedContracts/SignedContracts.store';
import useTeceoStyles from '../../../useTeceoStyles';
import { useOrderActionsContext } from '../contexts/OrderActions.context';
import { useSignatureContext } from './contexts/signature.context';
import { ModalContractPage, ModalContractReadPage } from './types';

type StoreViewerProps = {
  enterpriseCommercialName: string;
  enterpriseCompanyName: string;
};

const EnterpriseViewer = ({
  enterpriseCommercialName,
  enterpriseCompanyName,
}: StoreViewerProps) => {
  const { classes: teceoStyle } = useTeceoStyles();
  return (
    <Box>
      <Typography variant="h4" className={teceoStyle.variantBody1}>
        {enterpriseCommercialName}
      </Typography>
      <Typography color="textSecondary" variant="body2">
        {enterpriseCompanyName}
      </Typography>
    </Box>
  );
};

type ContractPageWrapperProps = {
  children: React.ReactNode;
};

const ContractPageWrapper = ({ children }: ContractPageWrapperProps) => {
  const { orderRaw } = useOrderActionsContext();
  const { page, personalInfo } = useSignatureContext();
  const { smallWidth } = useWidth();
  const { classes: teceoStyles } = useTeceoStyles();
  const { signedContracts } = useSignedContractsStore();
  const isContractHistoryPage = page === ModalContractPage.CONTRACT_HISTORY;
  const isCreateSignImagePage = page === ModalContractPage.CREATE_SIGN_IMAGE;
  const isMethodOfSignPage = page === ModalContractPage.METHOD_OF_SIGNING;
  const isAtHistoryContractAndEmpty =
    page === ModalContractPage.CONTRACT_HISTORY && signedContracts.length === 0;

  const hasToRenderDivider =
    (smallWidth && isContractHistoryPage) ||
    ModalContractReadPage.includes(page) ||
    isMethodOfSignPage ||
    isAtHistoryContractAndEmpty;

  if (isCreateSignImagePage) {
    return (
      <Box
        style={
          hasToRenderDivider
            ? {
                borderTop: '1px solid rgba(0,0,0, 0.16)',
              }
            : undefined
        }
        sx={{
          position: 'relative',
          height: '100%',
        }}
      >
        <Box
          sx={{
            pb: 3,
            px: smallWidth ? 3 : 4,
          }}
        >
          <Grid container>
            <Grid size={12}>
              <Typography variant="h4" className={teceoStyles.variantBody1}>
                {personalInfo?.name}
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
                xl: 2,
              }}
            >
              <Typography color="textSecondary" variant="body2">
                {personalInfo?.email}
              </Typography>
            </Grid>
            {personalInfo?.phone && (
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  xl: 2,
                }}
              >
                <Typography color="textSecondary" variant="body2">
                  {`+${personalInfo?.countryCode} ${personalInfo.phone.replace(
                    personalInfo.countryCode,
                    '',
                  )}`}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
        {children}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
      }}
    >
      <Box
        style={
          hasToRenderDivider
            ? {
                borderBottom: '1px solid rgba(0,0,0, 0.16)',
              }
            : undefined
        }
        sx={{
          pb: 3,
          px: smallWidth ? 3 : 4,
        }}
      >
        <Grid container spacing={smallWidth ? 1 : 3}>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <EnterpriseViewer
              enterpriseCommercialName={orderRaw?.brandCommercialName || ''}
              enterpriseCompanyName={orderRaw?.brandCompanyName || ''}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <EnterpriseViewer
              enterpriseCommercialName={orderRaw?.customerCommercialName || ''}
              enterpriseCompanyName={orderRaw?.customerCompanyName || ''}
            />
          </Grid>
        </Grid>
      </Box>
      {children}
    </Box>
  );
};

export default ContractPageWrapper;
